import { values } from 'lodash-es'
import {
    BrandContract,
    ItemCategoryLiteContract,
    LocationOrderChannelContract,
    LocationOrderFulfillmentTypeContract,
    MenuSlimContract,
    NotificationGroupContract,
    OrderFulfillmentStatus,
    OrderPaymentStatus,
    PosTerminalContract,
    PrinterSlimContract,
    ProductSlimModel,
} from 'models'
import { NodeProps, Node, XYPosition, Edge } from 'reactflow'
import { Dictionary } from 'utilities/typeUtility/typeUtility'

export interface EditorContext {
    offsetTop: number
    offsetLeft: number
}

export interface EditorController {
    setClientOffset: (top: number, left: number) => void

    /**
     * Create node and attach it into graph
     * @param type type of node (defined in `NodeInternalType`)
     * @param position
     * @param iData custom internal data for node
     * @returns
     */
    createNode: (
        type: NodeInternalType,
        position: XYPosition,
        iData?: any,
    ) => void

    updateNodeData: (
        node: string | Node<ElementData>,
        data: ElementData,
    ) => void
    replaceGraph: (graphData: any) => void
    clearGraph: () => void

    removeNode: (id: string, edgeIDs: string[]) => void

    getNodes: () => Node<ElementData, NodeInternalType>[]
    getEdges: () => Edge[]
    getNode: (id: string) => Node<ElementData, NodeInternalType> | undefined

    getContext: () => EditorContext
}

export interface EditorDataDict {
    orderChannels: LocationOrderChannelContract[]
    itemCategories: ItemCategoryLiteContract[]
    menus: MenuSlimContract[]
    brands: BrandContract[]
    fulfillments: LocationOrderFulfillmentTypeContract[]
    notificationGroups: NotificationGroupContract[]
    printers: PrinterSlimContract[]
    orderChannelsMap: Dictionary<LocationOrderChannelContract>
    itemCategoriesMap: Dictionary<ItemCategoryLiteContract>
    menusMap: Dictionary<MenuSlimContract>
    printerMap: Dictionary<PrinterSlimContract>
    brandsMap: Dictionary<BrandContract>
    notificationGroupsMap: Dictionary<NotificationGroupContract>
    fulfillmentsMap: Dictionary<LocationOrderFulfillmentTypeContract>
    orderFulfillmentStatus: OrderFulfillmentStatus[]
    orderPaymentStatus: OrderPaymentStatus[]
    products: ProductSlimModel[]
    posTerminals: PosTerminalContract[]
}

export interface ElementData {
    label?: string
    type?: string
    isNode?: boolean
    iData?: any
    displayText?: string
}

export type EventNodeData = {
    eventType: number
    params: any
}

export const FilterTypes = {
    OrderStatus: 'orderStatus',
    OrderChannel: 'orderChannel',
    OrderFulfillment: 'orderFulfillment',
    Menu: 'menu',
    ItemCategory: 'itemCategory',
    Item: 'itemId',
    Brand: 'brand',
    OrderFulfillmentStatus: 'orderFulfillmentStatus',
    OrderPaymentStatus: 'orderPaymentStatus',
    ContainItemInCategory: 'containItemInCategory',
}

/** List of available filters of order-level event */
export const orderScopeFilters = [
    FilterTypes.OrderStatus,
    FilterTypes.OrderChannel,
    FilterTypes.OrderFulfillment,
    FilterTypes.OrderFulfillmentStatus,
    FilterTypes.OrderPaymentStatus,
    FilterTypes.ContainItemInCategory,
]

/** List of available filters of order-line-level event */
export const orderLineScopeFilters = values(FilterTypes).filter(
    (f) => f !== FilterTypes.ContainItemInCategory,
)

export const FilterOperators = {
    Equal: 'EQ',
    AnyOf: 'AO',
    AnyExceptOf: 'AEO',
}

export interface FilterCondition {
    operandType: string
    operator: string
    value: any | any[]
}

export type FilterNodeData = {
    conditions: FilterCondition[]
}

export type ActionNodeData = {
    type: string
    value: any
    displayText: string
}

export interface CustomNodeProps<T> extends NodeProps<T> {
    id: string
    data: T
    type: string
    selected: boolean
}

export interface ActionNodeSettingProps {
    selectedNodeId: string
    controller: EditorController
    dataSource: EditorDataDict
}

export type NodeInternalType = 'event' | 'filter' | 'action' | undefined

export const EventType = {
    ORDER_STATUS_CHANGED: 1,
    ORDER_LINE_STATUS_CHANGED: 2,
}

export const defaultEditorContext: EditorContext = {
    offsetTop: 0,
    offsetLeft: 0,
}

export const NeededKDSActionType = {
    SendToKDS: 'sendToKDS',
    SendVoidOrderRequestToKDS: 'sendVoidOrderRequestToKDS',
}

export const ActionType = {
    ...NeededKDSActionType,
    Notification: 'notification',
    SendToPrintKitchenTicket: 'sendToPrintKitchenTicket',
}

type NeededKDSActionTypeKeys = keyof typeof NeededKDSActionType
type ActionTypeValues = typeof ActionType[keyof typeof ActionType]

// Type Guard Function
export function isNeededKDSActionType(
    value: ActionTypeValues,
): value is NeededKDSActionTypeKeys {
    return Object.values(NeededKDSActionType).includes(value as any)
}
